import type {
  DocumentFragment,
  TriageListItemFragment,
} from "@/__generated__/graphql";
import { useApolloClient } from "@apollo/client/react";
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MailIcon } from "lucide-react";
import {
  MotionGlobalConfig,
  motion,
  useMotionValue,
  useTransform,
} from "framer-motion";

import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import {
  getSelectableRowVariantClassName,
  SELECTABLE_LIST_INSET,
  SELECTABLE_ROW_HOVER_ATTR,
  SELECTABLE_ROW_INSET_CLASS,
  type SelectableListSize,
} from "@/components/molecules/SelectableRow";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { State } from "@/state";
import { DocumentObject } from "@/objects/DocumentObject";
import { Utils } from "@/utils";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import type { TriageBookLikePayment } from "./TriageBookLike";

import {
  TRIAGE_QUEUE_ROW_CLASS,
  TRIAGE_RECONCILE_ROW_PLACEHOLDER,
  type TriageCardDocument,
  TriageDocumentCard,
  TriageLinkBall,
  TriageMovedChip,
  TriageReconcileRow,
} from "./TriageReconcileRow";
import {
  type TriageBulkMatch,
  useTriageDetailContext,
} from "./TriageView.context";
import { TRIAGE_KEYS } from "./TriageView.keys";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

/**
 * Similar's match: what each of a payee's payments is lined up with - its
 * document (in the books, a chat's draft, or an email the mailboxes hold),
 * its transfer's other side, or the payment it copies - drawn in the panel
 * level with the payment's own row in the queue's place, which is left as it
 * is: the same list, its rows their own size, scrolled as it scrolls. What
 * none of them is lined up with waits under them. D takes the next of those
 * into the payment the keys are on, S the one before - or back to none -
 * and Backspace lets its own go; dragged onto another payment's row, one is
 * lined up with it, the one there taking its place, and onto what waits, it
 * is let go. Match - the toolbar's Book (*onLined*) - books each payment
 * ticked, and the payment open, with what is beside it, and imports each
 * email for the payment beside it.
 */

/** Loading, a typical document a line holds: a supplier's bill. */
const PLACEHOLDER_DOCUMENT: TriageCardDocument = {
  name: "Supplier invoice",
  reference: "INV-1042",
  date: "2026-01-15",
  amount: 128.4,
  currency: null,
  kind: "document",
  business: null,
};

/** Something a payment may be lined up with: a document in the books, a chat's draft of one, an email to import - or another payment. */
export type TriagePairedItem = {
  key: string;
  /** A document, a chat's draft of one, an email to import - or another payment: a transfer's other side, or the one it copies. */
  kind: "document" | "draft" | "email" | "transaction";
  /** Another payment's card in the queue: it is drawn as the payment beside it is. */
  card?: TriageListItemFragment | null;
  identifier: string;
  /** A document's, or a draft's, node: an email has none till it is read in. */
  documentId: string | null;
  reference: string | null;
  name: string;
  date: string | null;
  amount: number | null;
  currency: string | null;
  /** The payment it is lined up with as it opens: the one its rules most plainly pair it with, or it was found for. */
  pairedWith: string | null;
  /** Every payment its rules offer it to: two as likely for one are each offered to it, and paired with neither. */
  offeredTo: string[];
  /** An email is imported only for a payment of the business it was found for. */
  business: string | null;
  /** Whose it is, by name: as a payment's card says its business. */
  businessName: string | null;
  /** That business's node, to open it from the card. */
  businessId?: string | null;
  /** Who it is from: what goes with it where it is moved. */
  contact?: TriageCardDocument["contact"];
};

/** A payment's row in the queue's place, where it is in the list's content: what is lined up with it is drawn there. */
type TriagePairsSlot = {
  payment: string;
  top: number;
  height: number;
};

/**
 * How the list in the queue's place is laid out, by this column: how far down its first row starts unscrolled,
 * where its held row stops - at the top and at the foot - and how tall this column is.
 */
type TriagePairsFrame = {
  before: number;
  heldTop: number;
  heldBottom: number;
  height: number;
};

/** How a card lined up anew glides from where it was. */
const TRIAGE_PAIRS_GLIDE = {
  duration: 220,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

/** A line of what is unplaced with no list row to take its height from. */
const UNPLACED_LINE = 76;

/** A card carried by the pointer: what it is, where it was taken, where it is. */
type TriagePairsCarried = {
  key: string;
  element: HTMLElement;
  start: { x: number; y: number };
  isCarried: boolean;
};

export function TriageDocumentPairs({
  kind,
  paymentId,
  rows,
  pinned = null,
  ticked,
  keyed,
  list,
  onKeyed,
  size,
  onBack,
  onMatched,
  onLined,
  offered = null,
  onMatch,
  isLoading = false,
}: {
  /** The match picked: documents, transfers' other sides, or copies. */
  kind: TriageBulkMatch;
  /** The payment open: whose payee's others these are. */
  paymentId: string;
  /** Its payee's others, as the list shows them. */
  rows: TriageBookLikePayment[];
  /** The payment open itself: its own lined up and matched with theirs. */
  pinned?: TriageBookLikePayment | null;
  /** Those of them ticked: the pairs Match books. */
  ticked: ReadonlySet<string>;
  /** The payment the keys are on in the list, by identifier: D, S and Backspace change what is beside it. */
  keyed: string | null;
  /** The list in the queue's place: each payment's row there is where what it is lined up with is drawn. */
  list: HTMLElement | null;
  /** The list's keys put on a payment, by identifier - from this side, moving with it while the two are locked. */
  onKeyed: (identifier: string) => void;
  /** The list's size: its rows' inset. */
  size: SelectableListSize;
  onBack: () => void;
  /** Booked by their documents: off the queue. */
  onMatched: (identifiers: string[]) => void;
  /**
   * What the payments may be lined up with in place of their documents - each
   * transfer's other side, or the payment each copies - as the caller has
   * them: none asked for.
   */
  offered?: TriagePairedItem[] | null;
  /** Match, in place of matching documents: each payment and what is lined up beside it. */
  onMatch?: (pairs: { payment: string; item: TriagePairedItem }[]) => void;
  /** How many pairs Match would book, whether it is at it, and Match itself - for the toolbar's Book to press. */
  onLined: (lined: {
    count: number;
    isMatching: boolean;
    match: () => void;
  }) => void;
  /**
   * The payments, or what the caller offers them, still being read: each line
   * beside the list's rows a placeholder of what it would hold.
   */
  isLoading?: boolean;
}) {
  const apolloClient = useApolloClient();
  const pinnedId = pinned?.card.identifier ?? null;
  // Every payment lined up: the one open and its payee's others.
  const payments = useMemo(
    () =>
      new Map(
        [...(pinned ? [pinned] : []), ...rows].map((row) => [
          row.card.identifier,
          row,
        ]),
      ),
    [pinned, rows],
  );
  const { data } = useBackendQuery(Queries.TRIAGE_PAYEE_DOCUMENTS_QUERY, {
    variables: { id: paymentId, transactions: [...payments.keys()] },
    fetchPolicy: "cache-and-network",
    skip: offered != null || isLoading,
  });

  // Everything they may be lined up with: the documents, and each payment's
  // email in the mailboxes where it has no document of its own - or what the
  // caller offers in their place.
  const items = useMemo<TriagePairedItem[]>(() => {
    if (offered) return offered;
    const documents =
      data?.node?.__typename === "TransactionObject"
        ? (data.node.payeeDocuments ?? [])
        : [];
    return [
      ...documents.map(({ document, pairedWith, offeredTo }) => ({
        key: `document:${document.identifier}`,
        kind: document.pendingChange
          ? ("draft" as const)
          : ("document" as const),
        identifier: document.identifier,
        documentId: document.id,
        reference: document.reference ?? null,
        name:
          DocumentObject.getTitle(document as DocumentFragment) ?? "A document",
        date: document.dateIssued ?? null,
        amount:
          document.amountTotal != null ? Number(document.amountTotal) : null,
        currency: document.amountCurrency ?? null,
        pairedWith: pairedWith ?? null,
        offeredTo: offeredTo ?? [],
        business: document.entity?.identifier ?? null,
        businessName: document.entity?.name ?? null,
        businessId: document.entity?.id ?? null,
        contact: document.contact ?? null,
      })),
      ...[...payments.values()].flatMap((row) =>
        row.found
          ? [
              {
                key: `email:${row.found.identifier}`,
                kind: "email" as const,
                identifier: row.found.identifier,
                documentId: null,
                reference: null,
                name: row.found.subject ?? row.found.filename ?? "An email",
                date: row.found.sentAt,
                amount:
                  row.found.amount != null ? Number(row.found.amount) : null,
                currency: row.found.currency,
                pairedWith: row.card.identifier,
                offeredTo: [row.card.identifier],
                business: row.card.entity?.identifier ?? null,
                businessName: row.card.entity?.name ?? null,
              },
            ]
          : [],
      ),
    ];
  }, [data, offered, payments]);
  const byKey = useMemo(
    () => new Map(items.map((item) => [item.key, item])),
    [items],
  );

  // What each payment is lined up with, by payment: as its rules pair them -
  // worked out afresh as the payments and what they may pay arrive, whichever
  // comes first - till the person moves one; then as they left them.
  // The payment open lined up with what its suggestion picked offers - the
  // document, the other side, the copy - where that is one of these: as it
  // was picked before the line-up was opened.
  const { selections, triage } = useTriageDetailContext();
  const picked = (triage.suggestions ?? []).find(
    (suggestion) =>
      suggestion.identifier === selections.linesSuggestionIdentifier,
  );
  const pickedKey = (() => {
    if (!picked || !pinnedId) return null;
    const offers = [
      ...(picked.ruleSuggestionDocumentOptions ?? []).map((option) =>
        option.document ? `document:${option.document.identifier}` : null,
      ),
      ...(picked.ruleSuggestionTransactionOptions ?? []).map((option) =>
        option.transaction
          ? `transaction:${pinnedId}:${option.transaction.identifier}`
          : null,
      ),
    ];
    return (
      offers.find(
        (key) => key != null && items.some((item) => item.key === key),
      ) ?? null
    );
  })();
  const opening = useMemo(() => {
    const paired: Record<string, string | null> = {};
    if (pinnedId && pickedKey) paired[pinnedId] = pickedKey;
    for (const item of items) {
      // Lined up with the payment open as it was picked: with no other.
      if (item.key === pickedKey) continue;
      if (
        item.pairedWith &&
        payments.has(item.pairedWith) &&
        paired[item.pairedWith] == null
      ) {
        paired[item.pairedWith] = item.key;
      }
    }
    return paired;
  }, [items, payments, pickedKey, pinnedId]);
  const [pairs, setPairs] = useState<Record<string, string | null> | null>(
    null,
  );
  const pairing = pairs ?? opening;
  // How they were lined up before each change, the latest last.
  const historyRef = useRef<Record<string, string | null>[]>([]);
  // Lined up anew, each card glides from where it was to where it is: where
  // each was is held as the change is made, by the lines' own layer - so a
  // scroll in the same moment moves none - and each that moved is slid from
  // there once it is drawn where it now is. Nothing but a change glides.
  const layerRef = useRef<HTMLDivElement | null>(null);
  const placesRef = useRef<Map<string, { x: number; y: number }> | null>(null);
  const placeCards = () => {
    const layer = layerRef.current;
    const placed = new Map<string, { x: number; y: number }>();
    if (!layer) return placed;
    const base = layer.getBoundingClientRect();
    for (const card of layer.querySelectorAll<HTMLElement>(
      "[data-testid='triage-document-pairs-item']",
    )) {
      const rect = card.getBoundingClientRect();
      placed.set(card.dataset.key ?? "", {
        x: rect.left - base.left,
        y: rect.top - base.top,
      });
    }
    return placed;
  };
  const holdPlaces = () => {
    placesRef.current = placeCards();
  };
  useLayoutEffect(() => {
    const before = placesRef.current;
    placesRef.current = null;
    const layer = layerRef.current;
    if (!before || !layer || MotionGlobalConfig.skipAnimations) return;
    const after = placeCards();
    for (const [key, now] of after) {
      const was = before.get(key);
      if (!was) continue;
      const dx = was.x - now.x;
      const dy = was.y - now.y;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      layer
        .querySelector<HTMLElement>(`[data-key='${CSS.escape(key)}']`)
        ?.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: "translate(0, 0)" },
          ],
          TRIAGE_PAIRS_GLIDE,
        );
    }
  });
  const ownerOf = new Map(
    Object.entries(pairing).flatMap(([payment, key]) =>
      key && payments.has(payment) ? [[key, payment] as const] : [],
    ),
  );
  // What each is lined up with, by its business, told to the list in the
  // queue's place: a card lined up across two businesses draws the other's
  // half.
  const { setLinedUp } = useTriageDetailContext();
  const linedKey = JSON.stringify(
    [...payments.keys()].flatMap((payment) => {
      const item = byKey.get(pairing[payment] ?? "");
      const business =
        item?.card?.entity ??
        State.getState().entities.find(
          (entity) => entity.identifier === item?.business,
        );
      return business
        ? [
            [
              payment,
              {
                id: business.id,
                identifier: business.identifier,
                name: business.name,
              },
            ],
          ]
        : [];
    }),
  );
  useEffect(() => {
    setLinedUp?.(new Map(JSON.parse(linedKey)));
  }, [linedKey, setLinedUp]);
  useEffect(() => () => setLinedUp?.(null), [setLinedUp]);

  // *payment* lined up with *item* - the one there taking its place - or,
  // none, with nothing. An email goes only with a payment of the business it
  // was found for.
  const move = (payment: string, item: TriagePairedItem | null) => {
    const row = payments.get(payment);
    if (
      item?.kind === "email" &&
      item.business !== row?.card.entity?.identifier
    ) {
      State.getState().setSnackbarMessage(
        "An email found for one business's payment is imported for its own payments alone.",
      );
      return;
    }
    // What they were, for ⌘Z to put back.
    historyRef.current.push(pairing);
    holdPlaces();
    setPairs((was) => {
      const now = { ...(was ?? opening) };
      const left = now[payment] ?? null;
      now[payment] = item?.key ?? null;
      const there = item
        ? Object.entries(now).find(
            ([other, key]) => key === item.key && other !== payment,
          )?.[0]
        : undefined;
      if (there) now[there] = left;
      return now;
    });
  };
  // The next of what waits - or the one before - beside *payment*, and back
  // to none past either end.
  const cycle = (payment: string, direction: 1 | -1) => {
    const own = pairing[payment] ?? null;
    const choices: (TriagePairedItem | null)[] = [
      null,
      ...items.filter(
        (item) => !ownerOf.has(item.key) || ownerOf.get(item.key) === payment,
      ),
    ];
    const at = Math.max(
      0,
      choices.findIndex((choice) => (choice?.key ?? null) === own),
    );
    move(
      payment,
      choices[(at + direction + choices.length) % choices.length] ?? null,
    );
  };

  // Each payment's row in the queue's place, in order and as tall: the lines
  // here are a list of their own, moved as the list scrolls - by a transform,
  // as a table's header follows its rows - so what is beside a payment stays
  // level with its row, and the one the keys are on is held at the top or the
  // foot as its row is.
  const columnRef = useRef<HTMLDivElement | null>(null);
  const [slots, setSlots] = useState<TriagePairsSlot[]>([]);
  const [frame, setFrame] = useState<TriagePairsFrame>({
    before: 0,
    heldTop: 0,
    heldBottom: 0,
    height: 0,
  });
  const scrollTop = useMotionValue(0);
  const scroller = useMemo(() => {
    const row = list?.querySelector<HTMLElement>("[data-selectable-row]");
    for (
      let element = row?.parentElement ?? null;
      element && element !== list;
      element = element.parentElement
    ) {
      const overflow = getComputedStyle(element).overflowY;
      if (overflow === "auto" || overflow === "scroll") return element;
    }
    return null;
    // Read again as the list's rows are drawn - placeholders first.
  }, [list, payments]);
  useEffect(() => {
    const column = columnRef.current;
    if (!column || !scroller || !list) return;
    const measure = () => {
      const cards = [
        ...list.querySelectorAll<HTMLElement>(
          "[data-testid='triage-book-like-row'], [data-testid='triage-book-like-itself']",
        ),
      ];
      const rows = cards.map(
        (card) => card.closest<HTMLElement>("[data-selectable-row]") ?? card,
      );
      // Where each row is in the list's content, scrolled or not: by their
      // container, which is never held - the held row by the one before it,
      // or after it, as it is held off where it would be.
      const content = rows[0]?.parentElement?.getBoundingClientRect().top ?? 0;
      const placed = rows.map((row) => {
        const rect = row.getBoundingClientRect();
        return { top: rect.top - content, height: rect.height };
      });
      const next: TriagePairsSlot[] = [];
      cards.forEach((card, index) => {
        const payment = card.dataset.identifier;
        // One line a payment: a row going as another comes is drawn once.
        if (!payment || next.some((slot) => slot.payment === payment)) return;
        let { top } = placed[index];
        if (getComputedStyle(rows[index]).position === "sticky") {
          const before = placed[index - 1];
          const after = placed[index + 1];
          top = before
            ? before.top + before.height
            : after
              ? after.top - placed[index].height
              : top;
        }
        next.push({ payment, top, height: placed[index].height });
      });
      setSlots((was) =>
        was.length === next.length &&
        was.every(
          (slot, index) =>
            slot.payment === next[index].payment &&
            slot.top === next[index].top &&
            slot.height === next[index].height,
        )
          ? was
          : next,
      );
      const outer = scroller.getBoundingClientRect();
      const own = column.getBoundingClientRect();
      // Where the rows' container is in the list's content: each row is
      // placed by it.
      const container = rows[0]?.parentElement;
      const start = container
        ? container.getBoundingClientRect().top - outer.top + scroller.scrollTop
        : 0;
      const offset = outer.top - own.top;
      // Where the list holds its row: inside its scroller's padding, and the
      // row's own inset from its top and its foot.
      const held = rows.find(
        (row) => getComputedStyle(row).position === "sticky",
      );
      const padding = getComputedStyle(scroller);
      const inset = (side: "top" | "bottom") =>
        parseFloat(
          side === "top" ? padding.paddingTop : padding.paddingBottom,
        ) + (held ? parseFloat(getComputedStyle(held)[side]) || 0 : 0);
      const measured = {
        before: offset + start,
        heldTop: offset + inset("top"),
        heldBottom: own.bottom - outer.bottom + inset("bottom"),
        height: own.height,
      };
      setFrame((was) =>
        was.before === measured.before &&
        was.heldTop === measured.heldTop &&
        was.heldBottom === measured.heldBottom &&
        was.height === measured.height
          ? was
          : measured,
      );
    };
    measure();
    const resizes = new ResizeObserver(measure);
    const watchRows = () => {
      for (const row of scroller.querySelectorAll("[data-selectable-row]")) {
        resizes.observe(row);
      }
    };
    resizes.observe(scroller);
    resizes.observe(column);
    watchRows();
    const changes = new MutationObserver(() => {
      watchRows();
      measure();
    });
    changes.observe(scroller, { childList: true, subtree: true });
    const follow = () => scrollTop.set(scroller.scrollTop);
    follow();
    scroller.addEventListener("scroll", follow, { passive: true });
    return () => {
      resizes.disconnect();
      changes.disconnect();
      scroller.removeEventListener("scroll", follow);
    };
  }, [list, scroller, scrollTop]);
  // The lines, moved up as the list is scrolled.
  const linesY = useTransform(scrollTop, (top) => frame.before - top);
  // Scrolled here, the list scrolls: it is what both are drawn by.
  const scrollList = (by: number) => {
    if (scroller) scroller.scrollTop += by;
  };

  // What none of them is lined up with: more lines under the last of them,
  // each as tall as the list's plainest row - the keys reaching them from the
  // last line.
  const unplaced = items.filter((item) => !ownerOf.has(item.key));
  const lastBottom = slots.reduce(
    (bottom, slot) => Math.max(bottom, slot.top + slot.height),
    0,
  );
  const unplacedHeight = slots.length
    ? Math.min(...slots.map((slot) => slot.height))
    : UNPLACED_LINE;
  const unplacedLineTop = (at: number) => lastBottom + at * unplacedHeight;
  const room = unplaced.length * unplacedHeight;
  // Every place the keys stop on this side: each payment's line, then each
  // unplaced - ``unplaced:<key>``.
  const positions = [
    ...slots.map((slot) => slot.payment),
    ...unplaced.map((item) => `unplaced:${item.key}`),
  ];
  // What a payment is offered - those offered it first, then what is unplaced.
  const listOffered = (payment: string) =>
    items.filter((item) => item.offeredTo.includes(payment));
  const listCandidates = (payment: string) => {
    const offeredIt = listOffered(payment);
    return [
      ...offeredIt,
      ...unplaced.filter((item) => !offeredIt.includes(item)),
    ];
  };
  // Why a line is empty, in words.
  const sayWhyEmpty = (payment: string) => {
    const offeredIt = listOffered(payment);
    if (offeredIt.length >= 2) return `${offeredIt.length} equally likely`;
    if (offeredIt.length === 1) return "Nothing lined up";
    return kind === "transfers"
      ? "No other side found"
      : kind === "duplicates"
        ? "No copy found"
        : "No document found";
  };

  // A payment with no document has its mailboxes searched for one from its
  // empty line: said to be under way till it is back, and - nothing there -
  // that there is none. What they hold comes back with the payment and is
  // lined up beside it (``items``: each payment's email).
  const { mutate: searchForDocument } = useBackendMutation({
    mutation: Mutations.SEARCH_FOR_DOCUMENT,
  });
  const [mailboxesSearched, setMailboxesSearched] = useState<
    Record<string, "searching" | "searched">
  >({});
  // Asked for first, under its line: the words to look for, which may be
  // left out - Enter searches, Escape lets it be.
  const [askingEmail, setAskingEmail] = useState<{
    payment: string;
    words: string;
  } | null>(null);
  const askEmailSearch = (payment: string) =>
    setAskingEmail({ payment, words: "" });
  const searchMailboxes = (payment: string, query?: string) => {
    if (mailboxesSearched[payment] === "searching") return;
    setMailboxesSearched((was) => ({ ...was, [payment]: "searching" }));
    const settle = () =>
      setMailboxesSearched((was) => ({ ...was, [payment]: "searched" }));
    void searchForDocument({
      variables: {
        input: {
          transactionIdentifier: payment,
          connectionIdentifier: null,
          ...(query ? { query } : {}),
        },
      },
      callbacks: { onSuccess: settle, onError: settle },
    });
  };
  // Lined up by hand already, what a search found goes beside the payment it
  // was searched for, where that line is still empty.
  useEffect(() => {
    if (!pairs) return;
    const taken = new Set(Object.values(pairs));
    const found = items.filter(
      (item) =>
        item.kind === "email" &&
        item.pairedWith != null &&
        mailboxesSearched[item.pairedWith] != null &&
        !pairs[item.pairedWith] &&
        !taken.has(item.key),
    );
    if (found.length === 0) return;
    setPairs((was) => ({
      ...was,
      ...Object.fromEntries(found.map((item) => [item.pairedWith!, item.key])),
    }));
  }, [items, mailboxesSearched, pairs]);
  const isMirrored = State((state) => state.isTriageMirrored);

  // The keys on this side: a payment's line or what is unplaced - moving with
  // the list's while the two are locked.
  const [selected, setSelected] = useState<string | null>(keyed);
  const [isLocked, setIsLocked] = useState(true);
  const [wasKeyed, setWasKeyed] = useState(keyed);
  if (keyed !== wasKeyed) {
    setWasKeyed(keyed);
    if (isLocked && keyed) setSelected(keyed);
  }
  const [isFocused, setIsFocused] = useState(false);
  // Opened from the bulk list, which goes as this comes, the keys come here,
  // on the line of the payment the list's are on - not left on nothing.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    columnRef.current?.focus({ preventScroll: true });
  }, []);
  // A card carried by the keys: Space takes it up, ↑ ↓ carry it from line to
  // line, Space sets it down, Escape puts it back where it was.
  const [carried, setCarried] = useState<{
    key: string;
    before: Record<string, string | null>;
  } | null>(null);
  // A line's candidates, open under it: Enter opens them, ↑ ↓ pick, Enter
  // lines it up, Escape shuts them.
  const [choosing, setChoosing] = useState<{
    payment: string;
    at: number;
  } | null>(null);

  // *position* in sight: the list scrolled to it, both sides moving as one.
  const reveal = (position: string) => {
    if (!scroller) return;
    const slot = slots.find((one) => one.payment === position);
    const unplacedAt = unplaced.findIndex(
      (item) => `unplaced:${item.key}` === position,
    );
    const top = slot
      ? slot.top
      : unplacedAt >= 0
        ? unplacedLineTop(unplacedAt)
        : null;
    if (top == null) return;
    const height = slot?.height ?? unplacedHeight;
    const start = frame.before + top - frame.heldTop;
    const end = frame.before + top + height - (frame.height - frame.heldBottom);
    if (scroller.scrollTop > start) scroller.scrollTop = start;
    else if (scroller.scrollTop < end) scroller.scrollTop = end;
  };
  const select = (position: string | null) => {
    setSelected(position);
    if (!position) return;
    if (isLocked && !position.startsWith("unplaced:")) onKeyed(position);
    else reveal(position);
  };
  const undo = () => {
    const was = historyRef.current.pop();
    if (!was) return;
    holdPlaces();
    setPairs(was);
  };
  // The next empty line after the keys - or before them.
  const jumpEmpty = (direction: 1 | -1) => {
    const lines = slots.map((slot) => slot.payment);
    const at = lines.indexOf(selected ?? "");
    for (let step = 1; step <= lines.length; step += 1) {
      const payment =
        lines[(at + direction * step + lines.length * 2) % lines.length];
      if (payment && !pairing[payment]) {
        select(payment);
        return;
      }
    }
  };
  const jumpUnplaced = () => {
    if (unplaced[0]) select(`unplaced:${unplaced[0].key}`);
  };

  // What the keys do on this side.
  const onColumnKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    // Still read, nothing is lined up yet to move.
    if (isLoading) return;
    const at = positions.indexOf(selected ?? "");
    const position = selected;
    const isLine = position != null && !position.startsWith("unplaced:");
    const item = isLine
      ? (byKey.get(pairing[position] ?? "") ?? null)
      : (byKey.get((position ?? "").replace(/^unplaced:/, "")) ?? null);
    const done = () => {
      event.preventDefault();
      event.stopPropagation();
    };
    if (choosing) {
      const candidates = listCandidates(choosing.payment);
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        done();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setChoosing({
          ...choosing,
          at: Math.min(
            Math.max(choosing.at + step, 0),
            Math.max(candidates.length - 1, 0),
          ),
        });
      } else if (event.key === "Enter") {
        done();
        const chosen = candidates[choosing.at];
        if (chosen) move(choosing.payment, chosen);
        setChoosing(null);
      } else if (event.key === "Escape") {
        done();
        setChoosing(null);
      }
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      done();
      const step = event.key === "ArrowDown" ? 1 : -1;
      if (carried) {
        // Carried on to the next line: whatever was there takes its place.
        const lines = slots.map((slot) => slot.payment);
        const from = lines.indexOf(ownerOf.get(carried.key) ?? "");
        const to =
          from < 0
            ? step < 0
              ? lines.length - 1
              : -1
            : Math.min(Math.max(from + step, 0), lines.length - 1);
        const target = lines[to];
        const carriedItem = byKey.get(carried.key);
        if (target && carriedItem && target !== lines[from]) {
          move(target, carriedItem);
          select(target);
        }
        return;
      }
      if (event.shiftKey && isLine && item) {
        // Swapped with the line above or below.
        const lines = slots.map((slot) => slot.payment);
        const target = lines[lines.indexOf(position) + step];
        if (target) {
          move(target, item);
          select(target);
        }
        return;
      }
      const next =
        positions[Math.min(Math.max(at + step, 0), positions.length - 1)];
      if (next) select(next);
      return;
    }
    if (event.key === "ArrowLeft") {
      done();
      if (isLine) onKeyed(position);
      list
        ?.querySelector<HTMLElement>("[data-testid='triage-book-like-list']")
        ?.focus();
      return;
    }
    if (event.key === " ") {
      done();
      if (carried) setCarried(null);
      else if (item) setCarried({ key: item.key, before: pairing });
      return;
    }
    if (event.key === "Escape" && carried) {
      done();
      holdPlaces();
      setPairs(carried.before);
      setCarried(null);
      return;
    }
    if (event.key === "Enter") {
      done();
      if (isLine) setChoosing({ payment: position, at: 0 });
      else if (item && keyed) move(keyed, item);
      return;
    }
    if (event.key === "Backspace" && isLine && item) {
      done();
      move(position, null);
      return;
    }
    if (
      event.key.toLowerCase() === TRIAGE_KEYS.SEARCH_MAILBOXES &&
      isLine &&
      kind === "documents"
    ) {
      done();
      askEmailSearch(position);
    }
  };

  // Keys for both sides: → from the list to this side, ] [ to the next empty
  // line, g u to what is unplaced, \ the two locked or not, ⌘Z the last change
  // put back - and D, S, Backspace on the list's payment as before.
  const keysRef = useRef({
    keyed,
    cycle,
    move,
    undo,
    jumpEmpty,
    jumpUnplaced,
    select,
    pairing,
    askEmailSearch,
  });
  keysRef.current = {
    keyed,
    cycle,
    move,
    undo,
    jumpEmpty,
    jumpUnplaced,
    select,
    pairing,
    askEmailSearch,
  };
  useEffect(() => {
    let lastG = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input:not([type='checkbox']), textarea")) return;
      const inList = Boolean(target && list?.contains(target));
      const inColumn = Boolean(target && columnRef.current?.contains(target));
      if (!inList && !inColumn) return;
      const keys = keysRef.current;
      const done = () => {
        event.preventDefault();
        event.stopPropagation();
      };
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        done();
        keys.undo();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "]" || event.key === "[") {
        done();
        keys.jumpEmpty(event.key === "]" ? 1 : -1);
        return;
      }
      if (event.key === "\\") {
        done();
        setIsLocked((was) => !was);
        return;
      }
      if (event.key.toLowerCase() === "g") {
        lastG = Date.now();
        return;
      }
      if (event.key.toLowerCase() === "u" && Date.now() - lastG < 800) {
        done();
        keys.jumpUnplaced();
        columnRef.current?.focus();
        return;
      }
      if (!inList || event.shiftKey) return;
      if (event.key === "ArrowRight") {
        // To this side, on the line of the list's payment.
        done();
        if (keys.keyed) keys.select(keys.keyed);
        columnRef.current?.focus();
        return;
      }
      if (!keys.keyed) return;
      const key = event.key.toLowerCase();
      if (key === TRIAGE_KEYS.SEARCH_MAILBOXES && kind === "documents") {
        done();
        keys.askEmailSearch(keys.keyed);
        return;
      }
      if (key !== "d" && key !== "s" && event.key !== "Backspace") return;
      done();
      if (event.key === "Backspace") keys.move(keys.keyed, null);
      else keys.cycle(keys.keyed, key === "d" ? 1 : -1);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [kind, list]);

  // Where it stands, told to the toolbar - and how far past the payments it
  // reaches, to the list, to scroll on as far.
  const { setLineUpStatus, setLineUpRoom } = useTriageDetailContext();
  const statusRef = useRef({ askEmailSearch });
  statusRef.current = { askEmailSearch };
  // The payment the keys are on: this side's line, else the list's.
  const keyedPayment =
    selected && !selected.startsWith("unplaced:") ? selected : keyed;
  const searchesFor =
    kind === "documents" && keyedPayment ? keyedPayment : null;
  const isSearchingFor =
    searchesFor != null && mailboxesSearched[searchesFor] === "searching";
  useEffect(() => {
    setLineUpStatus?.({
      isLocked,
      toggleLock: () => setIsLocked((was) => !was),
      searchEmail: searchesFor
        ? () => statusRef.current.askEmailSearch(searchesFor)
        : null,
      isSearchingEmail: isSearchingFor,
    });
  }, [isLocked, isSearchingFor, searchesFor, setLineUpStatus]);
  useEffect(() => () => setLineUpStatus?.(null), [setLineUpStatus]);
  useEffect(() => {
    setLineUpRoom?.(room);
  }, [room, setLineUpRoom]);
  useEffect(() => () => setLineUpRoom?.(0), [setLineUpRoom]);

  // A card carried by the pointer: set down on a payment's row, lined up with
  // it; on what waits, let go.
  const carriedRef = useRef<TriagePairsCarried | null>(null);
  const [carrying, setCarrying] = useState<string | null>(null);
  const placeRef = useRef({ move });
  placeRef.current = { move };
  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const carried = carriedRef.current;
      if (!carried) return;
      const dx = event.clientX - carried.start.x;
      const dy = event.clientY - carried.start.y;
      if (!carried.isCarried) {
        // Taken up once it is moved a little.
        if (Math.hypot(dx, dy) < 6) return;
        carried.isCarried = true;
        setCarrying(carried.key);
      }
      carried.element.style.translate = `${dx}px ${dy}px`;
    };
    const onPointerUp = (event: PointerEvent) => {
      const carried = carriedRef.current;
      carriedRef.current = null;
      if (!carried) return;
      // Where it is set down, before it is taken back off the pointer: it
      // goes on into its line from there, not from where it was taken up.
      const shifted = carried.element.style.translate;
      const dropped = carried.element.getBoundingClientRect();
      carried.element.style.translate = "";
      if (!carried.isCarried) return;
      setCarrying(null);
      const { move: put } = placeRef.current;
      const item = byKeyRef.current.get(carried.key);
      // Set down on a payment's line: lined up with it.
      const line = document
        .elementsFromPoint(event.clientX, event.clientY)
        .find(
          (element): element is HTMLElement =>
            element instanceof HTMLElement &&
            element.dataset.testid === "triage-document-pairs-line",
        );
      if (item && line?.dataset.payment) put(line.dataset.payment, item);
      const base = layerRef.current?.getBoundingClientRect();
      if (placesRef.current && base) {
        placesRef.current.set(carried.key, {
          x: dropped.left - base.left,
          y: dropped.top - base.top,
        });
      } else if (shifted) {
        // Set down on none, or refused: back home from where it was let go.
        carried.element.animate(
          [{ translate: shifted }, { translate: "0px 0px" }],
          TRIAGE_PAIRS_GLIDE,
        );
      }
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, []);
  const byKeyRef = useRef(byKey);
  byKeyRef.current = byKey;
  const takeUp = (key: string) => (event: ReactPointerEvent<HTMLElement>) => {
    if (
      event.button !== 0 ||
      (event.target as HTMLElement).closest("button, [role='button']")
    ) {
      return;
    }
    event.preventDefault();
    carriedRef.current = {
      key,
      element: event.currentTarget,
      start: { x: event.clientX, y: event.clientY },
      isCarried: false,
    };
  };

  // Each pair of a ticked payment - and the payment open - booked or
  // imported at once: the documents' by matching them, the emails' by
  // reading them in for the payment beside them; another payment, as the
  // caller matches them.
  const { mutate: matchDocuments, loading: isMatching } = useBackendMutation({
    mutation: Mutations.MATCH_DOCUMENTS,
  });
  const { mutate: importFound, loading: isImporting } = useBackendMutation({
    mutation: Mutations.IMPORT_EMAIL_CONNECTION_SEARCH_RESULTS,
  });
  const lined = [...payments.keys()].flatMap((payment) => {
    const item = byKey.get(pairing[payment] ?? "");
    return item && (payment === pinnedId || ticked.has(payment))
      ? [{ payment, item }]
      : [];
  });
  const match = () => {
    if (lined.length === 0) return;
    if (onMatch) {
      onMatch(lined);
      onBack();
      return;
    }
    const documentPairs = lined.filter((pair) => pair.item.kind !== "email");
    const emailPairs = lined.filter((pair) => pair.item.kind === "email");
    if (documentPairs.length > 0) {
      matchDocuments({
        variables: {
          input: {
            pairs: documentPairs.map((pair) => ({
              transactionIdentifier: pair.payment,
              documentIdentifier: pair.item.identifier,
            })),
          },
        },
        callbacks: {
          onSuccess: (answer: {
            done: string[];
            refused: { transaction: string; reason: string }[];
          }) => {
            onMatched(answer.done);
            State.getState().setSnackbarMessage(
              answer.refused.length === 0
                ? `Matched ${answer.done.length} to their documents.`
                : `Matched ${answer.done.length}. ${answer.refused.length} not: ${answer.refused[0].reason}`,
            );
          },
          onError: () =>
            void apolloClient.refetchQueries({
              include: [Queries.TRIAGE_CONNECTION_QUERY],
            }),
        },
      });
    }
    if (emailPairs.length > 0) {
      importFound({
        variables: {
          input: {
            identifiers: emailPairs.map((pair) => pair.item.identifier),
            transactionIdentifiers: emailPairs.map((pair) => pair.payment),
          },
        },
      });
    }
    onBack();
  };
  // Its Match, the toolbar's Book: told as what it would book changes.
  const matchRef = useRef(match);
  matchRef.current = match;
  const isMatchingAny = isMatching || isImporting;
  useEffect(() => {
    onLined({
      count: lined.length,
      isMatching: isMatchingAny,
      match: () => matchRef.current(),
    });
  }, [lined.length, isMatchingAny, onLined]);

  // A row's inset, as the list's are, and a card as its row in the list is -
  // whether chosen or not, as the payments beside it are - its line darker
  // and lifted with the keys on this side, flat with them on the list's. In
  // a line's candidates, open under it, a row: a card only round the one
  // chosen.
  const cardClassName = (
    isActive: boolean,
    isKeysSide = isActive,
    isEveryRowCard = true,
  ) =>
    cn(
      getSelectableRowVariantClassName({
        size,
        variant: "outlined",
        layout: "cards",
        isActive,
        isFocusWithin: isKeysSide,
        isEveryRowCard,
      }),
      SELECTABLE_ROW_INSET_CLASS.cards[size],
      isEveryRowCard && TRIAGE_QUEUE_ROW_CLASS,
    );
  // What a payment is lined up with, as a card: its name, then what it is,
  // its day and its amount.
  const drawItemCard = (
    item: TriagePairedItem,
    row: TriageBookLikePayment | null,
    isSelected: boolean,
  ) => {
    // Linked to the payment beside it - matched as lined up: the payment
    // open, or one ticked.
    const isEngaged =
      row != null &&
      (row.card.identifier === pinnedId || ticked.has(row.card.identifier));
    return (
      <div
        data-testid="triage-document-pairs-item"
        data-key={item.key}
        data-carried={carrying === item.key || undefined}
        onPointerDown={takeUp(item.key)}
        className={cn(
          cardClassName(isSelected, isSelected && isFocused),
          // Set in from the line's edges as a list's card is.
          "flex-1 min-w-0 ml-1 mr-1",
          // Carried by the keys: a dashed ring round it, where it goes.
          carried?.key === item.key &&
            "[outline:2px_dashed_color-mix(in_srgb,var(--muted-foreground)_14%,var(--selectable-row-ground,transparent)_86%)] [outline-offset:0px]",
          "cursor-grab touch-none",
          // Taken up by the pointer: lifted over the rest, its own card's
          // ground under it still.
          carrying === item.key && "relative z-3 cursor-grabbing shadow-lg",
        )}
      >
        {item.kind === "transaction" ? (
          // Its card as the queue draws it - or, still read, that card's
          // placeholder.
          <TriageReconcileRow
            item={item.card ?? TRIAGE_RECONCILE_ROW_PLACEHOLDER}
            isLoading={item.card == null}
            onOpenPanel={null}
            linedUp={row ? { kind, business: row.card.entity } : null}
            link={
              row
                ? {
                    link: row.card.identifier,
                    end: "to",
                    isEngaged,
                    testId: "triage-link-to",
                  }
                : null
            }
          />
        ) : (
          // A document: drawn as a document is wherever it is offered the
          // payment - in its suggestions, or lined up here - mirrored as the
          // rest is.
          <TriageDocumentCard
            document={{
              name: item.name,
              reference: item.reference,
              date: item.date,
              amount: item.amount,
              currency: item.currency,
              kind: item.kind,
              business: {
                id: item.businessId ?? null,
                identifier: item.business,
                name: item.businessName,
              },
              contact: item.contact ?? null,
            }}
            businessIdentifier={row?.card.entity?.identifier}
            onView={
              item.documentId
                ? () =>
                    State.getState().pushToCommandStack({
                      commandDefinitionId: "update-DocumentObject",
                      context: {
                        nodeId: item.documentId,
                        entityIdentifier: item.business ?? undefined,
                        initialGroupId: "preview",
                      },
                    })
                : undefined
            }
            businessChip={
              // How the payment beside it is settled in another business: as
              // its suggestion says it, moved there already.
              row ? (
                <TriageMovedChip
                  testId="triage-proposal-settlement"
                  payment={row.card}
                />
              ) : undefined
            }
            movesTo={
              row?.card.belongsToEntity?.name &&
              item.business !== row.card.belongsToEntity.identifier
                ? {
                    id: row.card.belongsToEntity.id ?? null,
                    name: row.card.belongsToEntity.name,
                  }
                : null
            }
            // Matched as lined up: what it would do there, staged.
            isStaged={isEngaged}
            // The end of the line from the payment beside it.
            marker={
              row ? (
                <TriageLinkBall
                  link={row.card.identifier}
                  end="to"
                  isEngaged={isEngaged}
                  testId="triage-link-to"
                />
              ) : undefined
            }
            testIds={{
              crumbs: "triage-document-pairs-lead",
              date: "triage-document-pairs-date",
              title: "triage-document-pairs-name",
              amount: "triage-document-pairs-amount",
            }}
          />
        )}
      </div>
    );
  };

  // A line's candidates, open under it: those offered it first, then what is
  // unplaced - the one the keys are on raised.
  const drawCandidates = (payment: string) => {
    const candidates = listCandidates(payment);
    return (
      <div
        data-testid="triage-document-pairs-candidates"
        className="absolute top-full left-(--selectable-list-inset) right-(--selectable-list-inset) mt-1 p-1 flex flex-col gap-1 rounded-md [border-width:1px] [border-style:solid] border-border shadow-md bg-muted dark:bg-[#151719]"
      >
        {candidates.length === 0 ? (
          <Typography
            level="body-sm"
            className="text-muted-foreground/80 pl-2 pr-2 pt-1 pb-1"
          >
            Nothing to line up with it
          </Typography>
        ) : (
          candidates.map((item, at) => (
            <div
              key={item.key}
              data-testid="triage-document-pairs-candidate"
              data-key={item.key}
              data-chosen={choosing?.at === at || undefined}
              onMouseDown={(event) => {
                event.preventDefault();
                move(payment, item);
                setChoosing(null);
              }}
              className={cn(
                cardClassName(choosing?.at === at, choosing?.at === at, false),
                "cursor-pointer",
              )}
            >
              <Typography level="title-sm" className="truncate">
                {item.name}
              </Typography>
              <Typography
                level="body-xs"
                className="truncate text-muted-foreground/80"
              >
                {[
                  item.offeredTo.includes(payment) ? "Offered" : "Unplaced",
                  item.date ? Utils.fDate(new Date(item.date)) : null,
                  item.amount != null
                    ? Utils.fCurrency(item.amount, false, true, item.currency)
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="triage-document-pairs"
      // What a line to a card here is drawn only inside (``TriageLinks``).
      data-link-clip=""
      // Mirrored, as the person chose on the toolbar: each card's amount and
      // date first, what it is last, facing the payments beside them.
      data-triage-mirrored={isMirrored || undefined}
      onKeyDownCapture={(event) => {
        // A card carried, or a line's candidates open: Escape is theirs.
        if (event.key === "Escape" && !carried && !choosing && !askingEmail) {
          event.preventDefault();
          event.stopPropagation();
          onBack();
        }
      }}
      className="flex-1 min-h-0 flex flex-col bg-muted dark:bg-[#151719] [--selectable-row-ground:var(--muted)] dark:[--selectable-row-ground:#151719]"
      // The list's inset, as its rows are set in by it.
      style={
        {
          "--selectable-list-inset": `${SELECTABLE_LIST_INSET[size] * 8}px`,
        } as CSSProperties
      }
    >
      {/* Beside each payment's row: what it is lined up with. */}
      <div
        ref={columnRef}
        data-testid="triage-document-pairs-lines"
        tabIndex={0}
        onKeyDown={onColumnKeyDown}
        onFocus={() => {
          setIsFocused(true);
          if (!selected && keyed) setSelected(keyed);
        }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setIsFocused(false);
          }
        }}
        // Under the pointer, a card keeps still - only its title answers -
        // as the list's beside it do.
        {...{ [SELECTABLE_ROW_HOVER_ATTR]: "title" }}
        onWheel={(event) => scrollList(event.deltaY)}
        className="flex-1 min-h-0 relative overflow-hidden [outline:none]"
      >
        <motion.div ref={layerRef} style={{ y: linesY, position: "relative" }}>
          {slots.map((slot, index) => {
            const row = payments.get(slot.payment) ?? null;
            const item = byKey.get(pairing[slot.payment] ?? "") ?? null;
            const isKeyed = slot.payment === keyed;
            return (
              <motion.div
                key={slot.payment}
                data-testid="triage-document-pairs-line"
                data-payment={slot.payment}
                data-item={item?.key ?? ""}
                data-selected={slot.payment === selected || undefined}
                style={{ top: slot.top, height: slot.height }}
                // Clicked, this side's keys on it - and the list's, while the
                // two are locked - as ↑ ↓ here would put them.
                onClick={() => {
                  columnRef.current?.focus({ preventScroll: true });
                  select(slot.payment);
                }}
                className={cn(
                  "absolute left-0 right-0 pl-(--selectable-list-inset) pr-(--selectable-list-inset) box-border flex items-stretch",
                  // Its card the height of the list's beside it - from past
                  // the room between it and the one over it, where theirs
                  // starts past theirs (``TRIAGE_CARD_LIST_CLASS``), to the
                  // row's foot.
                  index === 0 ? "pt-0" : "pt-4",
                  // Each a layer of its own, so nothing in it - a chip's mark,
                  // lifted over the chip - is drawn over another.
                  "z-0",
                  // Over the others, the one the keys are on - scrolled with
                  // them, as its row in the list is.
                  isKeyed && "z-1",
                  // Its candidates open under it, over the lines below.
                  choosing?.payment === slot.payment && "z-4",
                  // The one a card is carried from, over every other - their
                  // cards, and what an empty one says - as it goes.
                  item != null && item.key === carrying && "z-5",
                )}
              >
                {isLoading ? (
                  // Still read: what the line would hold, as its placeholder.
                  <div
                    data-testid="triage-document-pairs-placeholder"
                    className={cn(
                      cardClassName(false),
                      "flex-1 min-w-0 ml-1 mr-1",
                    )}
                  >
                    {kind === "documents" ? (
                      <TriageDocumentCard
                        isLoading
                        document={PLACEHOLDER_DOCUMENT}
                        businessIdentifier={null}
                      />
                    ) : (
                      <TriageReconcileRow
                        item={TRIAGE_RECONCILE_ROW_PLACEHOLDER}
                        isLoading
                        onOpenPanel={null}
                        linedUp={null}
                        link={null}
                      />
                    )}
                  </div>
                ) : item && row ? (
                  drawItemCard(item, row, slot.payment === selected)
                ) : (
                  <>
                    {/* Nothing beside it, and why - in words where a
                        card's start, no card of its own: it glides nowhere
                        as cards are moved, and takes no pointer from them -
                        but for its one button. */}
                    <div
                      data-testid="triage-document-pairs-empty"
                      className={cn(
                        SELECTABLE_ROW_INSET_CLASS.cards[size],
                        TRIAGE_QUEUE_ROW_CLASS,
                        "box-border border border-transparent pointer-events-none [&_button]:pointer-events-auto",
                        "flex-1 min-w-0 ml-1 mr-1 flex items-start gap-2",
                      )}
                    >
                      <Typography
                        level="body-sm"
                        data-testid="triage-document-pairs-why"
                        className="text-muted-foreground/60 flex-1 min-w-0"
                      >
                        {mailboxesSearched[slot.payment] === "searching"
                          ? "Searching the mailboxes"
                          : mailboxesSearched[slot.payment] === "searched"
                            ? "Nothing in the mailboxes either"
                            : sayWhyEmpty(slot.payment)}
                      </Typography>
                      {/* The keys on it, its mailboxes searched for one. */}
                      {kind === "documents" &&
                      slot.payment === selected &&
                      mailboxesSearched[slot.payment] !== "searching" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="triage-document-pairs-search-mailboxes"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={(event) => {
                            event.stopPropagation();
                            askEmailSearch(slot.payment);
                          }}
                        >
                          Search mailboxes
                          <HotkeyDecorator
                            hotkey={TRIAGE_KEYS.SEARCH_MAILBOXES}
                            variant="outlined"
                            color="inherit"
                          />
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
                {choosing?.payment === slot.payment
                  ? drawCandidates(slot.payment)
                  : null}
                {askingEmail?.payment === slot.payment ? (
                  <div
                    data-testid="triage-document-pairs-email-words"
                    className="absolute top-full left-(--selectable-list-inset) right-(--selectable-list-inset) mt-1 z-4"
                  >
                    <InputGroup className="h-8">
                      <InputGroupAddon>
                        {<Icon ReactIcon={MailIcon} size="sm" />}
                      </InputGroupAddon>
                      <InputGroupInput
                        autoFocus
                        value={askingEmail.words}
                        placeholder="Words to look for in the mailboxes - or none"
                        onChange={(event) =>
                          setAskingEmail({
                            payment: slot.payment,
                            words: event.target.value,
                          })
                        }
                        onBlur={() => setAskingEmail(null)}
                        onKeyDown={(event) => {
                          // Typed here, a key is a word's - never the page's
                          // shortcut (L closing Similar, F asking again).
                          event.stopPropagation();
                          if (event.key !== "Enter" && event.key !== "Escape")
                            return;
                          event.preventDefault();
                          if (event.key === "Enter") {
                            searchMailboxes(
                              slot.payment,
                              askingEmail.words.trim() || undefined,
                            );
                          }
                          setAskingEmail(null);
                          columnRef.current?.focus({ preventScroll: true });
                        }}
                      />
                    </InputGroup>
                  </div>
                ) : null}
              </motion.div>
            );
          })}
          {/* What none of them is lined up with: more lines under the last
              of them, as the payments' are drawn. */}
          {unplaced.map((item, at) => (
            <div
              key={item.key}
              data-testid="triage-document-pairs-unplaced-line"
              data-item={item.key}
              data-selected={selected === `unplaced:${item.key}` || undefined}
              onClick={() => {
                columnRef.current?.focus({ preventScroll: true });
                select(`unplaced:${item.key}`);
              }}
              style={{ top: unplacedLineTop(at), height: unplacedHeight }}
              className={cn(
                "absolute left-0 right-0 pl-(--selectable-list-inset) pr-(--selectable-list-inset) box-border flex items-stretch",
                slots.length || at > 0 ? "pt-4" : "pt-0",
                item.key === carrying && "z-5",
              )}
            >
              {drawItemCard(item, null, selected === `unplaced:${item.key}`)}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
